import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FEATURE_TYPE,
  PREVENTIVE_INTERVAL_TYPE,
  VEHICLE_STATUS,
  type CreatePreventiveMaintenanceInput,
  type CreateVehicleFeatureInput,
  type FeatureType,
  type PreventiveIntervalType,
  type UpdateVehicleInput,
  type Vehicle,
  type VehicleStatus,
} from '@etnrlz/shared';
import {
  useCreateFeature,
  useCreatePreventive,
  useDeleteFeature,
  useFeatures,
  usePreventive,
  useUpdateVehicle,
  useVehicle,
  useVehicleOrders,
} from '../../lib/queries';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Textarea,
} from '../../components/ui';
import { Modal } from '../../components/Modal';
import { CreateOrderModal } from '../orders/OrdersPage';
import { PhotoUpload } from '../../components/PhotoUpload';
import { Icon } from '../../components/icons';
import {
  featureStatusMeta,
  featureTypeLabel,
  osStatusMeta,
  preventiveStatusMeta,
  vehicleStatusMeta,
} from '../../components/status';
import { formatDate, formatKm, formatMoney, pluralDays } from '../../lib/format';

export function VehicleDetailPage() {
  const { id = '' } = useParams();
  const { data: v, isLoading } = useVehicle(id);
  const features = useFeatures(id);
  const orders = useVehicleOrders(id);
  const preventive = usePreventive(id);
  const [featOpen, setFeatOpen] = useState(false);
  const [prevOpen, setPrevOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [osOpen, setOsOpen] = useState(false);

  if (isLoading || !v) return <Loading />;
  const meta = vehicleStatusMeta[v.status];

  return (
    <div>
      <Link to="/admin/vehicles" className="text-sm text-muted hover:text-text">
        ← Veículos
      </Link>
      <PageHeader
        title={v.apelido}
        subtitle={`${v.marca} ${v.modelo} · ${v.ano}${v.placa ? ` · ${v.placa}` : ''}`}
        actions={
          <>
            <Badge color={meta.color}>{meta.label}</Badge>
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
              Editar
            </Button>
          </>
        }
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-0 overflow-hidden">
          <div className="aspect-[16/10] bg-surface-2">
            {v.fotoCapa ? (
              <img src={v.fotoCapa} alt={v.apelido} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full grid place-items-center text-muted">
                <Icon name="car" className="h-12 w-12" />
              </div>
            )}
          </div>
          <div className="p-4 space-y-2 text-sm">
            <Row label="Km atual" value={formatKm(v.kmAtual)} />
            <Row label="Cor" value={v.cor ?? '—'} />
            <Row label="Chassi" value={v.chassi ?? '—'} mono />
            {v.observacoes && <p className="text-muted pt-2 border-t border-border">{v.observacoes}</p>}
            {v.fotos.length > 0 && (
              <div className="pt-3 border-t border-border">
                <div className="text-xs uppercase tracking-wide text-muted mb-2">Galeria</div>
                <div className="grid grid-cols-3 gap-2">
                  {v.fotos.map((f, i) => (
                    <img
                      key={i}
                      src={f.url}
                      alt={f.legenda ?? ''}
                      className="rounded-lg aspect-square object-cover bg-surface-2"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          {/* Atributos */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center gap-2">
                <Icon name="alert" className="h-4 w-4 text-accent" /> Atributos & vencimentos
              </h2>
              <Button size="sm" variant="secondary" onClick={() => setFeatOpen(true)}>
                <Icon name="plus" className="h-4 w-4" /> Adicionar
              </Button>
            </div>
            {!features.data || features.data.length === 0 ? (
              <EmptyState title="Nenhum atributo" hint="Seguro, PPF, IPVA, licenciamento…" />
            ) : (
              <div className="space-y-2">
                {features.data.map((f) => (
                  <div key={f.id} className="flex items-center justify-between rounded-lg bg-surface-2 px-3 py-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {featureTypeLabel[f.tipo] ?? f.tipo} — {f.label}
                      </div>
                      <div className="text-xs text-muted">
                        Vence {formatDate(f.dataVencimento)}
                        {f.valor != null ? ` · ${formatMoney(f.valor)}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge color={featureStatusMeta[f.status].color}>
                        {featureStatusMeta[f.status].label}
                      </Badge>
                      <DeleteFeatureButton vehicleId={id} fid={f.id} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Ordens */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center gap-2">
                <Icon name="wrench" className="h-4 w-4 text-accent" /> Ordens de serviço
              </h2>
              <Button size="sm" variant="secondary" onClick={() => setOsOpen(true)}>
                <Icon name="plus" className="h-4 w-4" /> Adicionar
              </Button>
            </div>
            {!orders.data || orders.data.length === 0 ? (
              <EmptyState title="Sem ordens" />
            ) : (
              <div className="space-y-2">
                {orders.data.map((o) => (
                  <Link
                    key={o.id}
                    to={`/admin/orders?os=${o.id}`}
                    className="flex items-center justify-between rounded-lg bg-surface-2 px-3 py-2 hover:bg-border/40 transition-colors"
                    title="Abrir a ordem de serviço"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{o.titulo}</div>
                      <div className="text-xs text-muted">
                        {pluralDays(o.diasParado)} · aberta {formatDate(o.abertaEm)}
                      </div>
                    </div>
                    <Badge color={osStatusMeta[o.status].color}>{osStatusMeta[o.status].label}</Badge>
                  </Link>
                ))}
              </div>
            )}
          </Card>

          {/* Preventivas */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center gap-2">
                <Icon name="clock" className="h-4 w-4 text-accent" /> Manutenção preventiva
              </h2>
              <Button size="sm" variant="secondary" onClick={() => setPrevOpen(true)}>
                <Icon name="plus" className="h-4 w-4" /> Adicionar
              </Button>
            </div>
            {!preventive.data || preventive.data.length === 0 ? (
              <EmptyState title="Nenhuma preventiva" />
            ) : (
              <div className="space-y-2">
                {preventive.data.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg bg-surface-2 px-3 py-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{p.titulo}</div>
                      <div className="text-xs text-muted">
                        {p.proximaData ? `Próx.: ${formatDate(p.proximaData)}` : ''}
                        {p.proximoKm ? ` · ${formatKm(p.proximoKm)}` : ''}
                      </div>
                    </div>
                    <Badge color={preventiveStatusMeta[p.status].color}>
                      {preventiveStatusMeta[p.status].label}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <AddFeatureModal vehicleId={id} open={featOpen} onClose={() => setFeatOpen(false)} />
      <AddPreventiveModal vehicleId={id} open={prevOpen} onClose={() => setPrevOpen(false)} />
      <EditVehicleModal vehicle={v} open={editOpen} onClose={() => setEditOpen(false)} />
      <CreateOrderModal vehicleId={id} open={osOpen} onClose={() => setOsOpen(false)} />
    </div>
  );
}

function EditVehicleModal({
  vehicle,
  open,
  onClose,
}: {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
}) {
  const update = useUpdateVehicle(vehicle.id);
  const [f, setF] = useState<UpdateVehicleInput>({});
  const cur = { ...vehicle, ...f };
  const set = (k: keyof UpdateVehicleInput, v: unknown) => setF((s) => ({ ...s, [k]: v }));

  const addGalleryPhoto = (url: string) => set('fotos', [...(cur.fotos ?? []), { url }]);
  const removeGalleryPhoto = (idx: number) =>
    set(
      'fotos',
      (cur.fotos ?? []).filter((_, i) => i !== idx),
    );

  const submit = async () => {
    await update.mutateAsync(f);
    onClose();
    setF({});
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Editar veículo"
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={update.isPending}>
            {update.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Field label="Apelido">
            <Input value={cur.apelido} onChange={(e) => set('apelido', e.target.value)} />
          </Field>
        </div>
        <Field label="Marca">
          <Input value={cur.marca} onChange={(e) => set('marca', e.target.value)} />
        </Field>
        <Field label="Modelo">
          <Input value={cur.modelo} onChange={(e) => set('modelo', e.target.value)} />
        </Field>
        <Field label="Ano">
          <Input type="number" value={cur.ano} onChange={(e) => set('ano', Number(e.target.value))} />
        </Field>
        <Field label="Placa">
          <Input value={cur.placa ?? ''} onChange={(e) => set('placa', e.target.value)} />
        </Field>
        <Field label="Cor">
          <Input value={cur.cor ?? ''} onChange={(e) => set('cor', e.target.value)} />
        </Field>
        <Field label="Km atual">
          <Input type="number" value={cur.kmAtual} onChange={(e) => set('kmAtual', Number(e.target.value))} />
        </Field>
        <Field label="Status">
          <Select value={cur.status} onChange={(e) => set('status', e.target.value as VehicleStatus)}>
            {VEHICLE_STATUS.map((st) => (
              <option key={st} value={st}>
                {vehicleStatusMeta[st].label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Chassi / VIN">
          <Input value={cur.chassi ?? ''} onChange={(e) => set('chassi', e.target.value)} />
        </Field>
        <div className="col-span-2">
          <Field label="Foto de capa">
            <PhotoUpload value={cur.fotoCapa} onChange={(url) => set('fotoCapa', url)} prefix={`vehicles/${vehicle.id}`} />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Observações">
            <Textarea value={cur.observacoes ?? ''} onChange={(e) => set('observacoes', e.target.value)} />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Galeria">
            <div className="flex flex-wrap gap-2 mb-2">
              {(cur.fotos ?? []).map((ph, i) => (
                <div key={i} className="relative">
                  <img src={ph.url} alt="" className="h-16 w-16 rounded-lg object-cover bg-surface-2" />
                  <button
                    onClick={() => removeGalleryPhoto(i)}
                    className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-st-danger text-white text-xs grid place-items-center"
                    aria-label="Remover foto"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <PhotoUpload onChange={addGalleryPhoto} prefix={`vehicles/${vehicle.id}`} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted">{label}</span>
      <span className={mono ? 'num' : ''}>{value}</span>
    </div>
  );
}

function DeleteFeatureButton({ vehicleId, fid }: { vehicleId: string; fid: string }) {
  const del = useDeleteFeature(vehicleId);
  return (
    <button
      onClick={() => del.mutate(fid)}
      className="text-muted hover:text-st-danger text-sm px-1"
      title="Remover"
    >
      ✕
    </button>
  );
}

function AddFeatureModal({ vehicleId, open, onClose }: { vehicleId: string; open: boolean; onClose: () => void }) {
  const create = useCreateFeature(vehicleId);
  const [f, setF] = useState<CreateVehicleFeatureInput>({
    tipo: 'SEGURO',
    label: '',
    dataVencimento: new Date().toISOString().slice(0, 10),
    lembreteDiasAntes: 30,
  });
  const set = (k: keyof CreateVehicleFeatureInput, v: unknown) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    await create.mutateAsync(f);
    onClose();
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Novo atributo"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={!f.label || create.isPending}>Salvar</Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <Field label="Tipo">
          <Select value={f.tipo} onChange={(e) => set('tipo', e.target.value as FeatureType)}>
            {FEATURE_TYPE.map((t) => (
              <option key={t} value={t}>{featureTypeLabel[t] ?? t}</option>
            ))}
          </Select>
        </Field>
        <Field label="Rótulo">
          <Input value={f.label} onChange={(e) => set('label', e.target.value)} placeholder="Seguro Porto" />
        </Field>
        <Field label="Fornecedor">
          <Input value={f.fornecedor ?? ''} onChange={(e) => set('fornecedor', e.target.value)} />
        </Field>
        <Field label="Vencimento">
          <Input type="date" value={f.dataVencimento} onChange={(e) => set('dataVencimento', e.target.value)} />
        </Field>
        <Field label="Valor (R$)">
          <Input type="number" value={f.valor ?? ''} onChange={(e) => set('valor', e.target.value ? Number(e.target.value) : undefined)} />
        </Field>
        <Field label="Avisar dias antes">
          <Input type="number" value={f.lembreteDiasAntes ?? 30} onChange={(e) => set('lembreteDiasAntes', Number(e.target.value))} />
        </Field>
      </div>
    </Modal>
  );
}

function AddPreventiveModal({ vehicleId, open, onClose }: { vehicleId: string; open: boolean; onClose: () => void }) {
  const create = useCreatePreventive(vehicleId);
  const [p, setP] = useState<CreatePreventiveMaintenanceInput>({
    titulo: '',
    tipoIntervalo: 'TEMPO',
    intervaloMeses: 6,
  });
  const set = (k: keyof CreatePreventiveMaintenanceInput, v: unknown) => setP((s) => ({ ...s, [k]: v }));
  const usaTempo = p.tipoIntervalo === 'TEMPO' || p.tipoIntervalo === 'AMBOS';
  const usaKm = p.tipoIntervalo === 'KM' || p.tipoIntervalo === 'AMBOS';
  const submit = async () => {
    await create.mutateAsync(p);
    onClose();
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nova preventiva"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={!p.titulo || create.isPending}>Salvar</Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Field label="Título">
            <Input value={p.titulo} onChange={(e) => set('titulo', e.target.value)} placeholder="Troca de óleo" />
          </Field>
        </div>
        <Field label="Intervalo por">
          <Select value={p.tipoIntervalo} onChange={(e) => set('tipoIntervalo', e.target.value as PreventiveIntervalType)}>
            {PREVENTIVE_INTERVAL_TYPE.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Select>
        </Field>
        <div />
        {usaTempo && (
          <>
            <Field label="A cada (meses)">
              <Input type="number" value={p.intervaloMeses ?? ''} onChange={(e) => set('intervaloMeses', e.target.value ? Number(e.target.value) : undefined)} />
            </Field>
            <Field label="Última feita (data)">
              <Input type="date" value={p.ultimaDataFeita ?? ''} onChange={(e) => set('ultimaDataFeita', e.target.value || undefined)} />
            </Field>
          </>
        )}
        {usaKm && (
          <>
            <Field label="A cada (km)">
              <Input type="number" value={p.intervaloKm ?? ''} onChange={(e) => set('intervaloKm', e.target.value ? Number(e.target.value) : undefined)} />
            </Field>
            <Field label="Último km feito">
              <Input type="number" value={p.ultimoKmFeito ?? ''} onChange={(e) => set('ultimoKmFeito', e.target.value ? Number(e.target.value) : undefined)} />
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}
